import React from "react";
import {
  Inbox,
  FolderOpen,
  CheckCircle2,
  Ban,
  ShoppingCart,
  PackageCheck,
  Truck,
  FilePlus2,
  Clock3,
  IndianRupee,
} from "lucide-react";
import { DashboardKpiItem } from "../components/Kpi/types";
import { SalesIndividualKpiApiItem } from "../services/salesIndividualDashboardApi";

const formatIndianCurrency = (value: number | string): string => {
  const numericValue = Number(value);
  if (!Number.isFinite(numericValue)) return String(value);

  if (numericValue >= 10000000) {
    const crores = numericValue / 10000000;
    return `₹${crores.toFixed(2)} Cr`;
  }
  if (numericValue >= 100000) {
    const lakhs = numericValue / 100000;
    return `₹${lakhs.toFixed(2)} L`;
  }

  return new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    maximumFractionDigits: 0,
  }).format(numericValue);
};

const formatKpiValue = (
  id: string,
  value: number | string,
): number | string => {
  if (id === "total_sales_value") return formatIndianCurrency(value);
  return value;
};

/**
 * Sales Individual KPIs
 * - `label`: confirmed KPI heading
 * - `helperLabel`: module-based meaning shown at bottom of card
 * Layout: 2 rows x 5 cards
 */
export const getSalesIndividualKpis = (): DashboardKpiItem[] => [
  {
    id: "new_enquiry",
    label: "New Enquiry",
    value: 42,
    helperLabel: "Total Enquiries in selected period",
    icon: React.createElement(Inbox, { size: 16 }),
    accentColor: "#2563eb",
    accentBg: "#eff6ff",
  },
  {
    id: "open_enquiry",
    label: "Open Enquiry",
    value: 28,
    helperLabel: "Active Enquiries currently open",
    icon: React.createElement(FolderOpen, { size: 16 }),
    accentColor: "#dc2626",
    accentBg: "#fef2f2",
  },
  {
    id: "quotation_won",
    label: "Quotation Won",
    value: 0,
    helperLabel: "Quotations with status PO Received",
    icon: React.createElement(CheckCircle2, { size: 16 }),
    accentColor: "#16a34a",
    accentBg: "#f0fdf4",
  },
  {
    id: "quotation_lost",
    label: "Quotation Lost",
    value: 9,
    helperLabel: "Quotations with status Lost",
    icon: React.createElement(Ban, { size: 16 }),
    accentColor: "#dc2626",
    accentBg: "#fef2f2",
  },
  {
    id: "orders_received",
    label: "Orders Received",
    value: 34,
    helperLabel: "Total Sales Orders received",
    icon: React.createElement(ShoppingCart, { size: 16 }),
    accentColor: "#0d9488",
    accentBg: "#f0fdfa",
  },
  {
    id: "orders_packed",
    label: "Orders Packed",
    value: 19,
    helperLabel: "Packing Completed orders",
    icon: React.createElement(PackageCheck, { size: 16 }),
    accentColor: "#16a34a",
    accentBg: "#ecfdf5",
  },
  {
    id: "orders_dispatched",
    label: "Orders Dispatched",
    value: 15,
    helperLabel: "Warehouse Dispatch completed",
    icon: React.createElement(Truck, { size: 16 }),
    accentColor: "#ea580c",
    accentBg: "#fff7ed",
  },
  {
    id: "po_raised",
    label: "PO Raised",
    value: 22,
    helperLabel: "Total Purchase Order raised",
    icon: React.createElement(FilePlus2, { size: 16 }),
    accentColor: "#0284c7",
    accentBg: "#f0f9ff",
  },
  {
    id: "po_pending",
    label: "PO Pending",
    value: 11,
    helperLabel: "Pending PO Inward",
    icon: React.createElement(Clock3, { size: 16 }),
    accentColor: "#d2363a",
    accentBg: "#fef2f2",
  },
  {
    id: "total_sales_value",
    label: "Total Sales Value",
    value: formatIndianCurrency(1245000),
    helperLabel: "Grand total amount from Sales Orders",
    icon: React.createElement(IndianRupee, { size: 16 }),
    accentColor: "#0f766e",
    accentBg: "#ecfdf5",
  },
];

export const mergeSalesIndividualKpis = (
  apiItems?: SalesIndividualKpiApiItem[],
): DashboardKpiItem[] => {
  const apiItemsById = new Map((apiItems || []).map((item) => [item.id, item]));

  return getSalesIndividualKpis().map((item) => {
    const apiItem = apiItemsById.get(item.id);
    if (!apiItem) return item;

    return {
      ...item,
      label: apiItem.label || item.label,
      value: formatKpiValue(apiItem.id, apiItem.value),
      rawValue: apiItem.value,
      helperLabel: apiItem.helper_label || apiItem.helperLabel || item.helperLabel,
    };
  });
};
